import type { mediaSchema } from "@/components/Forms/schema/mediaSchema";
import { type S3SignedUrl, useGetDocumentUrlMutation } from "@/mutations/document";
import {
    type CompanionCase,
    type NoteDetails,
    type NotifiedUserDetail,
    useFetchNoteDetails,
} from "@/queries/useFetchNoteDetails";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarIcon from "@mui/icons-material/CalendarToday";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import FolderIcon from "@mui/icons-material/Folder";
import LinkIcon from "@mui/icons-material/Link";
import PersonIcon from "@mui/icons-material/Person";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PushPinIcon from "@mui/icons-material/PushPin";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Link,
    Paper,
    Typography,
} from "@mui/material";
import { X } from "lucide-react";
import React from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import sanitizeHtml from "sanitize-html";
import type { z } from "zod";

const renderLine = (line: string) => {
    const clean = sanitizeHtml(line, {
        allowedTags: ["a", "hr", "br"],
        allowedAttributes: {
            a: ["href"],
        },
        allowedSchemes: ["http", "https", "mailto"],
    });

    if (clean !== line || clean.includes("<")) {
        return <span dangerouslySetInnerHTML={{ __html: clean }} />;
    }

    return line;
};

type Media = z.infer<typeof mediaSchema>;

interface NoteDetailModalProps {
    open: boolean;
    onClose: () => void;
    noteId: string | null;
    caseNumber: string;
    clientName: string;
}

type DateFormat = "short" | "numeric" | "attachment";

const formatDate = (dateString: string | null, format: DateFormat = "short"): string => {
    if (!dateString) {
        return format === "attachment" ? "" : "N/A";
    }
    const date = new Date(dateString);

    const options: Intl.DateTimeFormatOptions = {
        month: format === "numeric" ? "2-digit" : "short",
        day: format === "numeric" ? "2-digit" : "numeric",
        year: "numeric",
        hour: format === "short" ? "numeric" : "2-digit",
        minute: "2-digit",
        hour12: true,
    };

    let result = date.toLocaleString("en-US", options);

    if (format === "attachment") {
        result = result.replace(",", "").replace(" at ", " - ");
    }

    return result;
};

interface AttachmentItemProps {
    media: Media;
    noteCreatedAt: string | null;
    noteTag: string | null;
    noteCreatedBy: string;
    isLoading: boolean;
    onOpen: (attachmentId: string) => void;
}

const AttachmentItem: React.FC<AttachmentItemProps> = ({
    media,
    noteCreatedAt,
    noteTag,
    noteCreatedBy,
    isLoading,
    onOpen,
}) => {
    const handleClick = () => {
        onOpen(media.idUUID);
    };

    return (
        <Box
            onClick={handleClick}
            sx={{
                py: 2,
                px: 2,
                borderBottom: "1px solid #ccc",
                cursor: "pointer",
                "&:last-child": {
                    borderBottom: "none",
                },
                "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 2,
                    mb: 1,
                }}
            >
                <DescriptionIcon sx={{ fontSize: 32, color: "grey.600" }} />
                <Typography variant="body1" fontWeight={500}>
                    {media.filename || "Unnamed file"}
                </Typography>
                {isLoading && <CircularProgress size={20} />}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", ml: 6 }}>
                {formatDate(noteCreatedAt, "attachment")}
                {noteTag ? ` ${noteTag}` : ""}
                {noteCreatedBy ? ` : ${noteCreatedBy}` : ""}
            </Typography>
        </Box>
    );
};

const NotifiedUserCard: React.FC<{ user: NotifiedUserDetail }> = ({ user }) => {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                mb: 1,
                borderColor: user.isRead ? "divider" : "primary.main",
                borderWidth: user.isRead ? 1 : 2,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonOutlineIcon fontSize="small" color="action" />
                    <Typography variant="body1" fontWeight={600}>
                        {user.name}
                    </Typography>
                </Box>
                {user.role && (
                    <Chip
                        label={user.role}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.75rem" }}
                    />
                )}
            </Box>
            <Box sx={{ mt: 1, ml: 3.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body2" color="text.secondary">
                        Notified: {formatDate(user.notifiedAt, "numeric")}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CheckCircleOutlineIcon
                        sx={{
                            fontSize: 16,
                            color: user.isRead ? "success.main" : "warning.main",
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            color: user.isRead ? "success.main" : "warning.main",
                        }}
                    >
                        {user.isRead
                            ? `Read: ${formatDate(user.readAt, "numeric")}`
                            : "Not yet read"}
                    </Typography>
                </Box>
            </Box>
        </Paper>
    );
};

const CompanionCaseItem: React.FC<{ companionCase: CompanionCase }> = ({ companionCase }) => {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 1.5,
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
            }}
        >
            <FolderIcon color="primary" fontSize="small" />
            <Box sx={{ flex: 1 }}>
                <Link
                    component={RouterLink}
                    to={`/case-detail/${companionCase.caseNumber}`}
                    variant="body2"
                    fontWeight={600}
                    sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                >
                    #{companionCase.caseNumber}
                </Link>
                <Typography variant="caption" color="text.secondary" display="block">
                    {companionCase.clientName}
                </Typography>
            </Box>
            {companionCase.caseType && (
                <Chip
                    label={companionCase.caseType}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.7rem" }}
                />
            )}
        </Paper>
    );
};

const NoteDetailContent: React.FC<{ noteDetails: NoteDetails }> = ({ noteDetails }) => {
    const [loadingAttachmentId, setLoadingAttachmentId] = React.useState<string | null>(null);
    const getDocumentUrlMutation = useGetDocumentUrlMutation();
    const navigate = useNavigate();

    const handleLinkClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        if (target.tagName === "A") {
            const anchor = target as HTMLAnchorElement;
            const href = anchor.getAttribute("href");
            if (href?.startsWith("/")) {
                e.preventDefault();
                navigate(href);
            }
        }
    };

    const handleOpenAttachment = (attachmentId: string) => {
        setLoadingAttachmentId(attachmentId);
        getDocumentUrlMutation.mutate(attachmentId, {
            onSuccess: (data: S3SignedUrl) => {
                if (data.url) {
                    window.open(data.url, "_blank");
                }
                setLoadingAttachmentId(null);
            },
            onError: () => {
                setLoadingAttachmentId(null);
            },
        });
    };

    return (
        <Box sx={{ py: 1 }}>
            {/* Tag / Category */}
            {noteDetails.tag && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Category
                    </Typography>
                    <Chip label={noteDetails.tag} size="small" color="primary" variant="outlined" />
                </Box>
            )}

            {/* Note Content */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Note Content
                </Typography>
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        backgroundColor: "grey.50",
                        borderRadius: 1,
                    }}
                >
                    <Typography
                        variant="body1"
                        onClick={handleLinkClick}
                        sx={{
                            wordBreak: "break-word",
                            cursor: "auto",
                        }}
                    >
                        {noteDetails.note
                            ? noteDetails.note
                                  .replace(/\r/g, "")
                                  .split("\n")
                                  .map((line, i, arr) => {
                                      const cleanLine = line.trim();
                                      if (!cleanLine) {
                                          return <br key={i} />;
                                      }
                                      return (
                                          <React.Fragment key={i}>
                                              {renderLine(cleanLine)}
                                              {i !== arr.length - 1 && <br />}
                                          </React.Fragment>
                                      );
                                  })
                            : "No content"}
                    </Typography>
                </Paper>
            </Box>

            {/* Metadata Row */}
            <Box sx={{ mb: 3, display: "flex", gap: 3, flexWrap: "wrap" }}>
                <Box sx={{ flex: 1, minWidth: 150 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Created By
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                            {noteDetails.metaCreatedByPretty || noteDetails.noteBy || "Unknown"}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ flex: 1, minWidth: 150 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Created At
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                            {formatDate(noteDetails.metaCreatedWhen?.toISOString() ?? null)}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Task Association */}
            {noteDetails.caseTask && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Associated Task
                    </Typography>
                    <Chip label={noteDetails.caseTask.name} size="small" variant="outlined" />
                </Box>
            )}

            {/* Notified Users */}
            {noteDetails.notifiedUsersDetailed && noteDetails.notifiedUsersDetailed.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                        <PersonOutlineIcon fontSize="small" color="action" />
                        <Typography variant="subtitle2" color="text.secondary">
                            Notified Users
                        </Typography>
                    </Box>
                    {noteDetails.notifiedUsersDetailed.map((user) => (
                        <NotifiedUserCard key={user.id} user={user} />
                    ))}
                </Box>
            )}

            {/* Attachments */}
            {noteDetails.media.length > 0 && (
                <Box
                    sx={{
                        mb: 3,
                        backgroundColor: "#ddd",
                        borderRadius: "8px",
                        overflow: "hidden",
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{ fontWeight: 700, textAlign: "center", py: 2 }}
                    >
                        Attachments
                    </Typography>
                    {noteDetails.media.map((media) => (
                        <AttachmentItem
                            key={media.idUUID}
                            media={media}
                            noteCreatedAt={noteDetails.metaCreatedWhen?.toISOString() ?? null}
                            noteTag={noteDetails.tag}
                            noteCreatedBy={
                                noteDetails.metaCreatedByPretty || noteDetails.noteBy || ""
                            }
                            isLoading={loadingAttachmentId === media.idUUID}
                            onOpen={handleOpenAttachment}
                        />
                    ))}
                </Box>
            )}

            {/* Companion Cases */}
            {noteDetails.companionCases.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                        <LinkIcon fontSize="small" color="action" />
                        <Typography variant="subtitle2" color="text.secondary">
                            Companion Cases ({noteDetails.companionCases.length})
                        </Typography>
                    </Box>
                    {noteDetails.companionCases.map((companionCase) => (
                        <CompanionCaseItem
                            key={companionCase.caseId}
                            companionCase={companionCase}
                        />
                    ))}
                </Box>
            )}

            {/* Pinned Status */}
            {noteDetails.isPinned && (
                <Box sx={{ mb: 3 }}>
                    <Chip
                        icon={<PushPinIcon />}
                        label={"Pinned Note"}
                        size="small"
                        color="warning"
                    />
                </Box>
            )}
        </Box>
    );
};

const NoteDetailModal: React.FC<NoteDetailModalProps> = ({
    open,
    onClose,
    noteId,
    caseNumber,
    clientName,
}) => {
    const { data: noteDetails, isLoading, isError, error } = useFetchNoteDetails({ noteId });

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{
                "& .MuiDialog-paper": {
                    maxWidth: 800,
                    width: "100%",
                    maxHeight: "85vh",
                },
            }}
        >
            <DialogTitle>
                <Box sx={{ pr: 6 }}>
                    <Typography variant="h6" component="div">
                        Note Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Case{" "}
                        <Link
                            component={RouterLink}
                            to={`/case-detail/${caseNumber}`}
                            color="text.secondary"
                            sx={{ "&:hover": { color: "primary.main" } }}
                        >
                            #{caseNumber}
                        </Link>
                        {" - "}
                        {clientName}
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <X size={20} />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent>
                {isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : isError ? (
                    <Box
                        sx={{
                            p: 8,
                            textAlign: "center",
                            color: "text.secondary",
                        }}
                    >
                        <Typography variant="body1" color="error">
                            {error instanceof Error ? error.message : "Error loading note details"}
                        </Typography>
                    </Box>
                ) : !noteDetails ? (
                    <Box
                        sx={{
                            p: 8,
                            textAlign: "center",
                            color: "text.secondary",
                        }}
                    >
                        <Typography variant="body1">No note details available</Typography>
                    </Box>
                ) : (
                    <NoteDetailContent noteDetails={noteDetails} />
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NoteDetailModal;
